/**
 * Browser half of the presigned-POST upload (CASA 5.2.1, runtime/backend/utils/upload_guard.py).
 *
 * Every `…/upload-url` route answers `{ upload: { url, fields }, s3_key }`. The
 * `fields` carry a signed POST policy that pins the exact key, the exact
 * Content-Type and a content-length range, so the OBJECT STORE refuses a wrong
 * type or size (MinIO/S3 answer 403 AccessDenied, or 400 EntityTooLarge /
 * EntityTooSmall). The old presigned PUT enforced nothing.
 *
 * Fails closed: a response without a POST policy (e.g. an external provider
 * that still hands out a PUT URL) is refused rather than uploaded to.
 */
import { errorText } from './apiErrors';

export const UPLOAD_REFUSED = 'File type or size not allowed';
export const UPLOADS_UNAVAILABLE = 'Uploads are not available right now';
export const STORAGE_UNREACHABLE = 'The upload could not reach file storage. Please try again.';

function isPostPolicy(upload) {
    return Boolean(
        upload
        && typeof upload.url === 'string'
        && upload.url
        && upload.fields
        && typeof upload.fields === 'object'
        && !Array.isArray(upload.fields),
    );
}

export async function presignedUpload(upload, file) {
    if (!isPostPolicy(upload)) throw new Error(UPLOADS_UNAVAILABLE);
    const form = new FormData();
    Object.entries(upload.fields).forEach(([k, v]) => form.append(k, v));
    form.append('file', file); // must be last: S3 ignores fields after the file
    let res;
    try {
        res = await fetch(upload.url, { method: 'POST', body: form });
    } catch {
        // fetch rejects (TypeError "Failed to fetch") on a network or CORS
        // failure — e.g. a tenant bucket that has not been reprovisioned with
        // its CORS rule yet. Never toast the browser's raw text.
        throw new Error(STORAGE_UNREACHABLE);
    }
    if (!res.ok) {
        throw new Error(res.status === 400 || res.status === 403 ? UPLOAD_REFUSED : 'Upload failed');
    }
}

/**
 * A toast-safe sentence for any failure along the upload path: an axios
 * rejection from the runtime (413/415/403 carry `{code, message}`) or an Error
 * thrown by `presignedUpload`. Always a string (see apiErrors.js).
 */
export function uploadErrorText(err, fallback) {
    if (err?.response) return errorText(err, fallback);
    return typeof err?.message === 'string' && err.message ? err.message : fallback;
}
